import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from '../../components/ui/sheet';
import { Skeleton } from '../../components/ui/skeleton';
import axios from 'axios';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { optimizedSrc, optimizedSrcSet } from '../../lib/image-cdn';
import { Search, ArrowUpDown, Filter, Package, ChevronLeft, ChevronRight, Lightbulb, ArrowRight } from 'lucide-react';
import InlineSearchAutocomplete from '../../components/InlineSearchAutocomplete';
import { getProductUrl, getCollectionRoute, getCollectionSlug } from '../../utils/productUrl';
import SEO, { generateBreadcrumbSchema } from '../../components/SEO';

// Import catalog components
import { QuickSpecsBar } from '../../components/catalog/QuickSpecsBar';
import { ActiveChipsBar } from '../../components/catalog/ActiveChipsBar';
import { FacetSidebar } from '../../components/catalog/FacetSidebar';
import { ScopeHeader } from '../../components/catalog/ScopeHeader';
import { CollectionShowcase } from '../../components/collection/CollectionShowcase';
import { CatalogueIntro } from '../../components/catalog/CatalogueIntro';
import collectionContent from '../../data/collection-showcases.json';
import applicationScenes from '../../data/application-scenes.json';
import FacadeGallery from '../../components/catalog/FacadeGallery';

// Import hook
import useCatalogFilters from '../../hooks/useCatalogFilters';
import { useCatalogueResults } from '../../hooks/useCatalogueResults';
import { MobileCollectionSelector } from '../../components/catalog/MobileCollectionSelector';
import { CatalogPagination } from '../../components/catalog/CatalogPagination';

/**
 * Unified Product Listing Shell
 * Handles ALL catalog entry points: collection, category, mounting, application, global
 * Uses consistent filtering engine with scope-aware facets
 */
export default function ProductListingShell({ initialScope = null, defaultCollection = null }) {
  const { collection: collectionParam } = useParams();
  const [desktopFilters, setDesktopFilters] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktopFilters(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const filters = useCatalogFilters(getCollectionSlug(collectionParam) || defaultCollection || initialScope?.collection);
  const navigate = useNavigate();
  const changeCollection = collection => {
    const params = new URLSearchParams(window.location.search);
    ['collection', 'page', 'tronix_category_slug', 'flex_category_slug'].forEach(key => params.delete(key));
    navigate({ pathname: collection ? `/${collection}` : '/products/catalog', search: params.toString() });
  };
  
  const { products, total, loading, error, facetOptions, retry, grouped, countUnit } = useCatalogueResults(filters);
  
  // UI state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Get scope label for header
  const getScopeLabel = () => {
    if (filters.scopeType === 'collection') {
      const coll = facetOptions.collections.find(c => c.slug === filters.collection);
      return coll?.name || filters.collection.toUpperCase();
    }
    if (filters.scopeType === 'category') {
      return filters.category.replaceAll('-', ' ').toUpperCase();
    }
    if (filters.scopeType === 'mounting') {
      const mt = facetOptions.mountingTypes.find(m => m.slug === filters.mounting);
      return mt?.name || filters.mounting.replace('-', ' ').toUpperCase();
    }
    if (filters.scopeType === 'application') {
      return filters.application.replace('-', ' ').toUpperCase();
    }
    return 'All Products';
  };

  // Handler wrappers
  const handleCategoryChange = (slug) => {
    filters.setQsCategory(slug);
    filters.setPage(1);
  };

  const handleCutoutRangeToggle = (rangeLabel) => {
    const current = filters.qsCutoutRanges;
    filters.setQsCutoutRanges(
      current.includes(rangeLabel) ? current.filter(r => r !== rangeLabel) : [...current, rangeLabel]
    );
    filters.setPage(1);
  };

  const handleWattageRangeToggle = (rangeLabel) => {
    const current = filters.qsWattageRanges;
    if (current.includes(rangeLabel)) {
      filters.setQsWattageRanges(current.filter(r => r !== rangeLabel));
    } else {
      filters.setQsWattageRanges([...current, rangeLabel]);
    }
    filters.setPage(1);
  };

  const handleCCTToggle = (cct) => {
    const current = filters.qsCCTs;
    if (current.includes(cct)) {
      filters.setQsCCTs(current.filter(c => c !== cct));
    } else {
      filters.setQsCCTs([...current, cct]);
    }
    filters.setPage(1);
  };

  const handleIPToggle = (ip) => {
    const current = filters.qsIPRatings;
    if (current.includes(ip)) {
      filters.setQsIPRatings(current.filter(i => i !== ip));
    } else {
      filters.setQsIPRatings([...current, ip]);
    }
    filters.setPage(1);
  };

  const handleDimmingChange = (value) => {
    filters.setQsDimming(value);
    filters.setPage(1);
  };

  const handleMountingChange = (slug) => {
    filters.setMounting(slug);
    filters.setPage(1);
  };

  const handleCollectionToggle = (slug, checked) => {
    // Note: This is for sidebar multi-select, not scope
    // In collection scope, this is hidden
    filters.setPage(1);
  };

  const handleApplicationToggle = (slug, checked) => {
    filters.setPage(1);
  };

  const handleFamilyToggle = (family, checked) => {
    const current = filters.families;
    if (checked) {
      filters.setFamilies([...current, family]);
    } else {
      filters.setFamilies(current.filter(f => f !== family));
    }
    filters.setPage(1);
  };

  const totalPages = Math.ceil(total / filters.perPage);

  // Section 9 / indexation policy: a single-category filter view is a duplicate of
  // its selection guide, so it points there and stays out of the index.
  const activeCategory = filters.qsCategory || filters.category;
  // Indexation policy (audit Section 4): filtered, sorted and paginated views stay
  // out of the index; the selection guide is the indexable landing page.
  const isFilteredView = (() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return [...params.keys()].some(k => !k.startsWith('utm_') && !['gclid', 'fbclid'].includes(k));
  })();

  // Crawlable pagination (audit Section 4): every page has a real URL
  const pageHref = (n) => {
    const params = new URLSearchParams(window.location.search);
    if (n > 1) params.set('page', String(n)); else params.delete('page');
    const qs = params.toString();
    return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  };
  const [briefCanonical, setBriefCanonical] = useState(null);
  useEffect(() => {
    if (!activeCategory) { setBriefCanonical(null); return; }
    let live = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${activeCategory}/brief`)
      .then((res) => live && setBriefCanonical(res.data?.canonical || null))
      .catch(() => live && setBriefCanonical(null));
    return () => { live = false; };
  }, [activeCategory]);

  const collectionLabel = filters.collection 
    ? filters.collection.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'All Products';

  const isWallWasher = filters.qsCategory === 'wall-washer' && filters.collection === 'lumi-flex';
  const isNeonFlex = filters.qsCategory === 'neon-flex' && filters.collection === 'lumi-flex';
  const collectionEditorial = collectionContent[filters.collection];
  const collectionHero = collectionEditorial ? applicationScenes[collectionEditorial.photos[0]] : null;
  const heroSource = isWallWasher ? '/images/catalog/hero-wall-washer.jpg' : isNeonFlex ? '/images/catalog/hero-neon-flex.jpg' : collectionHero?.src || '/images/catalog/hero-bg.png';

  return (
    <div className="min-h-screen bg-background pt-[72px]" data-testid="product-listing-shell">
      <SEO
        url={briefCanonical || undefined}
        robots={isFilteredView || !filters.collection ? 'noindex, follow' : undefined}
        title={collectionEditorial ? `${collectionEditorial.name} — ${collectionEditorial.category}` : `${collectionLabel} - LED Lighting Products`}
        description={collectionEditorial ? `${collectionEditorial.name}: ${collectionEditorial.category.toLowerCase()}. Explore product families, application inspiration and model-specific specifications.` : `Browse ${collectionLabel.toLowerCase()} luminaires by power, colour temperature, IP rating, cut-out size and mounting type. Open full specifications and request project pricing.`}
        keywords={`${collectionLabel}, LED lighting, architectural lighting, ${filters.collection || 'products'}`}
        type="product.group"
        schema={generateBreadcrumbSchema([
          { name: 'Home', url: `${process.env.REACT_APP_SITE_ORIGIN}/` },
          { name: 'Products', url: `${process.env.REACT_APP_SITE_ORIGIN}/products` },
          ...(filters.collection ? [{ name: collectionLabel, url: `${process.env.REACT_APP_SITE_ORIGIN}/${filters.collection}` }] : []),
        ])}
      />
      <CatalogueIntro scope={{ collection: filters.collection, category: filters.category, mounting: filters.mounting,
        application: filters.application, familyName: filters.families.length ? filters.families.map(prettifySlug).join(' · ') : undefined }}
        titleTestId="catalog-heading" summary={loading ? 'Loading catalogue…' : error ? 'Catalogue temporarily unavailable' : `${total.toLocaleString()} ${countUnit === 'models' ? 'models' : 'product families'}`} />

      {/* COLLECTION NAVIGATION - Below hero, 2 symmetrical lines */}
      <div className="border-b bg-background/95 backdrop-blur-sm lg:sticky lg:top-[72px] z-40">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-3">
          {/* First Row - 6 main collections */}
          <MobileCollectionSelector collection={filters.collection} onChange={changeCollection} />
          <div className="hidden lg:flex flex-wrap items-center justify-center gap-2 mb-2">
            <button
              onClick={() => changeCollection('lumi-indoor')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-indoor' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-indoor"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">INDOOR</span>
              </span>
            </button>
            
            <button
              onClick={() => changeCollection('lumi-outdoor')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-outdoor' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-outdoor"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">OUTDOOR</span>
              </span>
            </button>
            
            <button
              onClick={() => changeCollection('lumi-pro')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-pro' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-pro"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">PRO</span>
              </span>
            </button>
            
            <button
              onClick={() => changeCollection('lumi-arc')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-arc' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-arc"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">ARC</span>
              </span>
            </button>
            
            <button
              onClick={() => changeCollection('lumi-flex')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-flex' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-flex"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">FLEX</span>
              </span>
            </button>
            
            <button
              onClick={() => changeCollection('lumi-ville')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-ville' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-ville"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">VILLE</span>
              </span>
            </button>
          </div>

          {/* Second Row - 5 additional collections */}
          <div className="hidden lg:flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => changeCollection('lumi-deco')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-deco' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-deco"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">DECO</span>
              </span>
            </button>

            <button
              onClick={() => changeCollection('lumi-brick')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-brick' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-brick"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">BRICK</span>
              </span>
            </button>

            <button
              onClick={() => changeCollection('lumi-tronix')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-tronix' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-tronix"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">TRONIX</span>
              </span>
            </button>

            <button
              onClick={() => changeCollection('lumi-sys')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-sys' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-sys"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">SYS</span>
              </span>
            </button>

            <button
              onClick={() => changeCollection('lumi-acoustic')}
              className={`px-4 py-2 border rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                filters.collection === 'lumi-acoustic' 
                  ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white' 
                  : 'border-border hover:border-[hsl(var(--accent))]'
              }`}
              data-testid="quick-nav-acoustic"
            >
              <span className="heading-mixed text-xs pointer-events-none">
                <span className="thin">LUMI</span> <span className="bold">ACOUSTIC</span>
              </span>
            </button>
          </div>
        </div>
        
        {/* Location Helper Banner - Show for categories spanning indoor/outdoor */}
        {filters.category && !filters.collection && ['wall-light', 'ceiling-light', 'floor-light', 'down-light', 'spot-light', 'track-light', 'ceiling-recessed-light', 'bollard-light'].includes(filters.category) && (
          <div className="border-t bg-accent/5">
            <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-3">
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
                <span className="text-muted-foreground">💡 Narrow by location:</span>
                <span className="text-foreground/90">Click</span>
                <span className="inline-flex items-center px-2 py-1 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 rounded text-[hsl(var(--accent))] font-semibold text-xs">
                  LUMI INDOOR
                </span>
                <span className="text-foreground/90">or</span>
                <span className="inline-flex items-center px-2 py-1 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 rounded text-[hsl(var(--accent))] font-semibold text-xs">
                  LUMI OUTDOOR
                </span>
                <span className="text-foreground/90">above to filter by application area</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scope Header - Only show when filtered (not on main catalog) */}
      {(
        <ScopeHeader
          scopeType={filters.scopeType}
          scopeValue={filters.collection || filters.category || filters.mounting || filters.application}
          scopeLabel={getScopeLabel()}
          onExpandScope={filters.expandScope}
          resultCount={total}
          countUnit={countUnit}
          error={error}
          loading={loading}
          hasIntro
        />
      )}

      <div id="catalogue-results" className="scroll-mt-44" />

      {/* Quick Specs Bar */}
      <QuickSpecsBar
        categories={facetOptions.categories}
        wattageRanges={facetOptions.wattageRanges}
        cctOptions={facetOptions.cctOptions}
        ipRatings={facetOptions.ipRatings}
        cutoutRanges={facetOptions.cutoutRanges}
        dimmingOptions={facetOptions.dimmingOptions}
        selectedCategory={filters.qsCategory}
        selectedWattageRanges={filters.qsWattageRanges}
        selectedCCTs={filters.qsCCTs}
        selectedIPRatings={filters.qsIPRatings}
        selectedCutoutRanges={filters.qsCutoutRanges}
        selectedDimming={filters.qsDimming}
        onCategoryChange={handleCategoryChange}
        onWattageRangeToggle={handleWattageRangeToggle}
        onCCTToggle={handleCCTToggle}
        onIPToggle={handleIPToggle}
        onCutoutRangeToggle={handleCutoutRangeToggle}
        onDimmingChange={handleDimmingChange}
        disabled={loading}
      />

      {/* Selection guide for the active category filter (Section 9 brief) */}
      {briefCanonical && (
        <div className="border-b bg-[#FAFAFA]">
          <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-3 text-sm flex items-center gap-2 flex-wrap">
            <Lightbulb className="w-4 h-4 text-[#888]" />
            <span className="text-muted-foreground">How to choose in this category:</span>
            <Link to={briefCanonical} className="font-semibold underline hover:no-underline" data-testid="catalog-brief-link">
              read the selection guide
            </Link>
          </div>
        </div>
      )}

      {/* Active Filters Chips */}
      <h2 className="sr-only">Filters</h2>
      <ActiveChipsBar
        chips={filters.activeChips}
        onRemoveChip={filters.removeChip}
        onClearAll={filters.clearFilters}
      />

      {/* Utility Bar */}
      <div className="border-b bg-background relative z-20">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            {/* Search */}
            <InlineSearchAutocomplete
              value={filters.search}
              onChange={(val) => filters.setSearch(val)}
              onPageReset={() => filters.setPage(1)}
                className="flex-1 min-w-0 w-full max-w-md"
            />

            {/* Sort, View, and Mobile Filter Button */}
            <div className="flex flex-wrap gap-2">
              <Select 
                value={filters.sort} 
                onValueChange={(val) => {
                  filters.setSort(val);
                  filters.setPage(1);
                }}
              >
                <SelectTrigger aria-label="Sort products" className="w-[140px] h-9" data-testid="catalog-sort-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="catalog-sort-relevance" value="relevance">Relevance</SelectItem>
                  <SelectItem data-testid="catalog-sort-name" value="name">Name (A-Z)</SelectItem>
                  <SelectItem data-testid="catalog-sort-name-desc" value="name-desc">Name (Z-A)</SelectItem>
                  <SelectItem data-testid="catalog-sort-variants" value="variants">Most Variants</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Items per page selector */}
              <Select 
                value={filters.perPage.toString()}
                onValueChange={(val) => {
                  filters.setPerPage(parseInt(val));
                  filters.setPage(1);
                }}
              >
                <SelectTrigger aria-label="Products per page" className="w-[110px] h-9" data-testid="catalog-perpage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {![24, 48, 96].includes(filters.perPage) && <SelectItem data-testid="catalog-perpage-custom" value={String(filters.perPage)}>{filters.perPage} items</SelectItem>}
                  <SelectItem data-testid="catalog-perpage-24" value="24">24 items</SelectItem>
                  <SelectItem data-testid="catalog-perpage-48" value="48">48 items</SelectItem>
                  <SelectItem data-testid="catalog-perpage-96" value="96">96 items</SelectItem>
                </SelectContent>
              </Select>

              {/* Mobile Filter Sheet */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="lg:hidden h-9" 
                    data-testid="catalog-filters-open-button"
                  >
                    <Filter className="w-4 h-4" />
                    <span className="sr-only sm:not-sr-only">Filters{filters.activeFiltersCount > 0 && ` (${filters.activeFiltersCount})`}</span>
                  </Button>
                </SheetTrigger>
                <SheetContent data-testid="catalog-mobile-filter-sheet" side="left"
                  className="w-[300px] max-w-full sm:w-[360px] h-[100dvh] max-h-[100dvh] flex flex-col gap-4 overflow-hidden z-[70] data-[state=open]:duration-150 data-[state=closed]:duration-150 data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0"
                  style={{ '--tw-enter-translate-x': '0', '--tw-exit-translate-x': '0' }}>
                  <SheetHeader className="shrink-0">
                    <SheetTitle data-testid="catalog-mobile-filter-title">Filters</SheetTitle>
                    <SheetDescription data-testid="catalog-mobile-filter-description" className="sr-only">Filter catalogue results by category and technical specifications.</SheetDescription>
                  </SheetHeader>
                  <div data-testid="catalog-mobile-filter-body" className="min-h-0 flex-1 overflow-hidden">
                    <FacetSidebar
                      fillHeight
                      mountingTypes={facetOptions.mountingTypes}
                      categories={facetOptions.categories}
                      families={facetOptions.families}
                      selectedMounting={filters.mounting}
                      selectedCategory={filters.qsCategory}
                      selectedFamilies={filters.families}
                      onMountingChange={handleMountingChange}
                      onCategoryChange={handleCategoryChange}
                      onFamilyToggle={handleFamilyToggle}
                      scopeType={filters.scopeType}
                      currentCollection={filters.collection}
                      disabled={loading}
                    />
                  </div>
                  <div data-testid="catalog-mobile-filter-footer" className="shrink-0 pt-2" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                    <SheetClose asChild>
                      <Button data-testid="catalog-mobile-filter-apply" className="w-full">{error ? 'Close filters' : loading ? 'Loading products…' : `Show ${total} ${countUnit}`}</Button>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block col-span-3">
            {desktopFilters && !mobileFiltersOpen &&
            <div className="sticky top-[280px]">
              <FacetSidebar
                mountingTypes={facetOptions.mountingTypes}
                categories={facetOptions.categories}
                families={facetOptions.families}
                selectedMounting={filters.mounting}
                selectedCategory={filters.qsCategory}
                selectedFamilies={filters.families}
                onMountingChange={handleMountingChange}
                onCategoryChange={handleCategoryChange}
                onFamilyToggle={handleFamilyToggle}
                scopeType={filters.scopeType}
                currentCollection={filters.collection}
                disabled={loading}
              />
            </div>
            }
          </div>

          {/* Product Grid */}
          <div className="col-span-12 lg:col-span-9">
            {/* Collection Showcase - Show when in global view with no active filters AND no search */}
            {!loading && filters.scopeType === 'global' && filters.activeFiltersCount === 0 && !filters.debouncedSearch && (
              <div className="mb-12" data-testid="collection-showcase">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold tracking-tight mb-2">Explore by Collection</h2>
                  <p className="text-sm text-muted-foreground">
                    Browse our specialized lighting collections, each designed for specific applications
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {/* LUMI INDOOR - Priority Collection */}
                  <Link
                    to="/lumi-indoor"
                    className="group relative h-[280px] rounded-lg overflow-hidden border border-border hover:border-[hsl(var(--accent))] transition-all duration-300 hover:shadow-lg"
                    data-testid="collection-card-indoor"
                  >
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1773565199252-2a4614f9a403?w=800)',
                        filter: 'brightness(0.4)'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/30" />
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <h2 className="heading-mixed text-3xl text-white mb-2">
                        <span className="thin">LUMI</span> <span className="bold">INDOOR</span>
                      </h2>
                      <p className="text-white/80 text-sm mb-3">Premium indoor architectural lighting</p>
                      <div className="flex items-center text-white/90 text-xs font-medium">
                        <span>Explore Collection</span>
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                  
                  {/* LUMI OUTDOOR - Priority Collection */}
                  <Link
                    to="/lumi-outdoor"
                    className="group relative h-[280px] rounded-lg overflow-hidden border border-border hover:border-[hsl(var(--accent))] transition-all duration-300 hover:shadow-lg"
                    data-testid="collection-card-outdoor"
                  >
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1773539040820-d2d0531b3fb8?w=800)',
                        filter: 'brightness(0.4)'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/30" />
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <h2 className="heading-mixed text-3xl text-white mb-2">
                        <span className="thin">LUMI</span> <span className="bold">OUTDOOR</span>
                      </h2>
                      <p className="text-white/80 text-sm mb-3">Weather-resistant outdoor solutions</p>
                      <div className="flex items-center text-white/90 text-xs font-medium">
                        <span>Explore Collection</span>
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                  
                  {/* LUMI DECO */}
                  <Link
                    to="/lumi-deco"
                    className="group relative h-[220px] rounded-lg overflow-hidden border border-border hover:border-[hsl(var(--accent))] transition-all duration-300 hover:shadow-lg"
                    data-testid="collection-card-deco"
                  >
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1761157993834-13ad2799f662?w=600)',
                        filter: 'brightness(0.4)'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/30" />
                    <div className="relative h-full p-5 flex flex-col justify-end">
                      <h2 className="heading-mixed text-2xl text-white mb-2">
                        <span className="thin">LUMI</span> <span className="bold">DECO</span>
                      </h2>
                      <p className="text-white/80 text-xs mb-2">Decorative lighting fixtures</p>
                      <div className="flex items-center text-white/90 text-xs font-medium">
                        <span>Explore</span>
                        <ArrowRight className="w-3 h-3 ml-2 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                  
                  {/* LUMI FLEX */}
                  <Link
                    to="/lumi-flex"
                    className="group relative h-[220px] rounded-lg overflow-hidden border border-border hover:border-[hsl(var(--accent))] transition-all duration-300 hover:shadow-lg"
                    data-testid="collection-card-flex"
                  >
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1570402290298-143753dadf0e?w=600)',
                        filter: 'brightness(0.4)'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-900/60 to-black/40" />
                    <div className="relative h-full p-5 flex flex-col justify-end">
                      <h2 className="heading-mixed text-2xl text-white mb-2">
                        <span className="thin">LUMI</span> <span className="bold">FLEX</span>
                      </h2>
                      <p className="text-white/80 text-xs mb-2">Flexible strip & neon lighting</p>
                      <div className="flex items-center text-white/90 text-xs font-medium">
                        <span>Explore</span>
                        <ArrowRight className="w-3 h-3 ml-2 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </div>
                
                <Separator className="my-8" />
                
                <div className="text-center mb-6">
                  <h2 className="text-lg font-semibold mb-2">Or browse all products below</h2>
                  <p className="text-sm text-muted-foreground">
                    {total.toLocaleString()} architectural lighting solutions available
                  </p>
                </div>
              </div>
            )}
            
            {/* Loading State */}
            {loading && (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-grid-loading">
                {[...Array(12)].map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-3 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && error && <div role="alert" data-testid="catalog-load-error" className="border border-red-200 p-6 space-y-4"><p>{error}</p><div className="flex flex-wrap gap-3"><Button data-testid="catalog-retry-button" onClick={retry}>Try again</Button><Button data-testid="catalog-error-clear-button" variant="outline" onClick={filters.clearFilters}>Clear filters</Button></div></div>}
            {!loading && !error && products.length === 0 && (
              <Card className="p-12 text-center" data-testid="catalog-zero-results">
                <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h2 className="text-lg font-semibold mb-2">No products found</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {filters.activeFiltersCount > 0
                    ? "Try adjusting your filters to see more results"
                    : "No products available at the moment"}
                </p>
                {filters.activeFiltersCount > 0 && (
                  <div className="flex gap-2 justify-center">
                    <Button onClick={filters.clearFilters} variant="outline" data-testid="catalog-clear-filters-button">
                      Clear all filters
                    </Button>
                    {filters.scopeType !== 'global' && (
                      <Button onClick={filters.expandScope} variant="outline" data-testid="catalog-expand-scope-button">
                        Expand to all products
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Product Grid */}
            {!loading && !error && products.length > 0 && (
              <>
                <h2 className="sr-only">Product results</h2>
                <div className="balanced-grid listing-grid" data-testid="product-grid">
                  {products.map((product) => {
                    // For grouped families: use family_slug for multi-article, canonical_slug for single
                    const slug = product.product_count > 1
                      ? (product.family_slug || product.family?.toLowerCase().replace(/\s+/g, '-'))
                      : (product.canonical_slug || product.family_slug);
                    const familyUrl = (!grouped && product.model_path) || product.canonical_path || (slug && product.primary_collection
                      ? `/${product.primary_collection}/${slug}`
                      : '/products/catalog');
                    const cardId = familyUrl.replaceAll('/', '--');

                    return (
                      <Card 
                        key={familyUrl}
                        className="catalog-result-card overflow-hidden h-full bg-card border-border hover:border-[hsl(var(--accent))]/50 transition-colors duration-200"
                        data-testid={`product-card-${cardId}`}
                      >
                        {/* Product Image - Clickable to family page */}
                        <Link data-testid={`product-image-link-${cardId}`} to={familyUrl} aria-label={`${product.family || product.name || product.base_code} ${product.primary_category_name || 'luminaire'} — view specifications`}>
                          <div className="aspect-[4/3] bg-white relative overflow-hidden flex items-center justify-center p-4" data-image-surface="product">
                            {product.primary_image ? (
                              <img
                                data-testid={`product-img-${cardId}`}
                                src={optimizedSrc(product.primary_image, { w: 500 })}
                                srcSet={optimizedSrcSet(product.primary_image, [200, 300, 500, 800])}
                                sizes="(max-width: 640px) 45vw, (max-width: 1280px) 30vw, 300px"
                                width={500}
                                height={500}
                                loading="lazy"
                                decoding="async"
                                alt={[product.family, product.base_code, product.primary_category_name || product.primary_category, 'LUMIBRIGHT architectural luminaire'].filter(Boolean).join(' ')}
                                className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                                onError={(e) => {
                                  // Missing/case-mismatched files fall back to a
                                  // placeholder icon instead of a broken-image alt.
                                  const parent = e.currentTarget.parentElement;
                                  e.currentTarget.style.display = 'none';
                                  if (parent && !parent.querySelector('[data-image-fallback]')) {
                                    const fb = document.createElement('div');
                                    fb.setAttribute('data-testid', `product-image-fallback-${cardId}`);
                                    fb.setAttribute('data-image-fallback', 'true');
                                    fb.setAttribute('data-image-surface', 'product');
                                    fb.className = 'w-full h-full flex items-center justify-center text-muted-foreground text-xs uppercase tracking-widest';
                                    fb.textContent = 'Image unavailable';
                                    parent.appendChild(fb);
                                  }
                                }}
                              />
                            ) : (
                              <div data-testid={`product-image-unavailable-${cardId}`} className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                                <Lightbulb className="w-12 h-12 text-muted-foreground" />
                                <span className="text-xs">Image unavailable</span>
                              </div>
                            )}
                            {/* Product count badge for multi-article families */}
                            {product.product_count > 1 ? (
                              <div data-testid={`product-card-variants-${cardId}`} className="absolute top-2 right-2 max-w-[calc(100%-1rem)] bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">
                                {product.product_count} products · {product.variant_count} variants
                              </div>
                            ) : product.variant_count > 1 ? (
                              <div data-testid={`product-card-variants-${cardId}`} className="absolute top-2 right-2 max-w-[calc(100%-1rem)] bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">
                                {product.variant_count} variants
                              </div>
                            ) : null}
                          </div>
                        </Link>
                        
                        <CardContent className="p-4">
                          {/* Collection label */}
                          {product.primary_collection && (
                            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-700 mb-1.5">
                              {product.primary_collection.replace('lumi-', '')}
                            </div>
                          )}
                          
                          {/* Family name - Primary title */}
                        <Link data-testid={`product-title-link-${cardId}`} to={familyUrl} aria-label={`${product.family || product.name || product.base_code} ${product.primary_category_name || 'luminaire'} — view specifications`}>
                            <h3 className="font-semibold text-sm mb-1 line-clamp-1 hover:text-[hsl(var(--accent))] transition-colors leading-snug" data-testid={`product-card-title-${cardId}`}>
                              {grouped ? product.family || product.name : product.name}
                            </h3>
                          </Link>
                          
                          {/* Category + base code */}
                          <p data-testid={`product-card-code-${cardId}`} className="text-xs text-muted-foreground mb-3 break-words">
                            {product.primary_category_name || product.primary_category?.replace(/-/g, ' ')} {product.base_code && `· ${product.base_code}`}
                          </p>
                          
                          {/* Quick specs badges */}
                          <div className="flex gap-1 flex-wrap" data-testid={`product-card-specs-${cardId}`}>
                            {product.power_numeric && (
                              <Badge data-testid={`product-card-power-${cardId}`} variant="outline" title={product.power_range?.map(v => `${v}W`).join(', ')} className="text-[10px] font-mono border-neutral-300 text-neutral-700">
                                {product.power_range && product.power_range.length > 1 
                                  ? product.power_range.slice(0, 3).map(v => `${v}W`).join(' / ') + (product.power_range.length > 3 ? ` +${product.power_range.length - 3}` : '')
                                  : `${product.power_numeric}W`}
                              </Badge>
                            )}
                            {product.ip_rating_normalized && (
                              <Badge data-testid={`product-card-ip-${cardId}`} variant="outline" className="text-[10px]">
                                {product.ip_rating_normalized}
                              </Badge>
                            )}
                            {product.cct_values && product.cct_values.length > 0 && (
                              <Badge data-testid={`product-card-cct-${cardId}`} variant="outline" title={product.cct_values.map(c => `${c}K`).join(', ')} className="text-[10px]">
                                {product.cct_values.slice(0, 3).map(c => `${c}K`).join(' / ') + (product.cct_values.length > 3 ? ` +${product.cct_values.length - 3}` : '')}
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination */}
                <CatalogPagination page={filters.page} totalPages={totalPages} pageHref={pageHref} onChange={filters.setPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
